import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ChevronDown, Upload } from 'lucide-react';
import confetti from 'canvas-confetti';
import PlaysThisWeekHero from '../components/PlaysThisWeekHero';
import Header from '../components/Header';
import Footer from '../components/Footer';
import RecentFiles from '../components/RecentFiles';
import Hero from '../components/Hero';
import CommunityPool from '../components/CommunityPool';
// import WhatsNew from '../components/WhatsNew'; // Hidden to reduce noise
import HomeTopSlot from '../components/HomeTopSlot';
import { QuickStats } from '../components/QuickStats';
import PersonalStorageCard from '../components/PersonalStorageCard';
import { RecentCollections } from '../components/RecentCollections';
import UploadZone from '../components/UploadZone';
import UnifiedUploadCard from '../components/UnifiedUploadCard';
import HeroPlayerDemo from '../components/HeroPlayerDemo';
import { SignInToClaimBanner } from '../components/SignInToClaimBanner';
import { ClaimedFilesCollectionPrompt, useClaimedFilesPrompt } from '../components/ClaimedFilesCollectionPrompt';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { PRO_STORAGE_GB, PRO_STORAGE_HOURS_320KBPS } from '../config/plans';
import { useUpload } from '../contexts/UploadContext';
import { useExpiringFiles } from '../hooks/useExpiringFiles';
import { useUserFiles } from '../contexts/UserFilesContext';
import { useCollections } from '../contexts/CollectionsContext';

// Lazy load heavy components to improve initial load time
const CommunityViewsChart = lazy(() => import('../components/CommunityViewsChart'));

const Home: React.FC = () => {
  const { theme } = useTheme();
  const { isAuthenticated, user, isBetaUser, isProUser } = useAuth();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  // Post-claim "organize into a collection" prompt. Held here for the render
  // site below the dashboard grid; HomeTopSlot consults the same hook to
  // suppress itself while the prompt is showing.
  const { prompt: claimedFilesPrompt, clear: clearClaimedFilesPrompt } = useClaimedFilesPrompt();

  // Upload context for anonymous users
  const { queue, addFiles, retryFile, removeFile } = useUpload();

  // Drag and drop state for anonymous users
  const [isDragging, setIsDragging] = useState(false);

  // Shared user-files cache — single fetch reused by HomeTopSlot,
  // RecentFiles, QuickStats, PlaysThisWeekHero.
  const { files: userFiles, loading: loadingUserFiles, refresh: refreshUserFiles } = useUserFiles();
  const { refresh: refreshCollections } = useCollections();
  const { expiringFiles, count: expiringCount } = useExpiringFiles(userFiles);

  // Refresh on every Home mount so back-navigation from /player gets fresh data
  // (mirrors the old "fetch on mount" behavior of RecentFiles, but coalesced via
  // the context's in-flight dedup so it's still a single HTTP call per endpoint).
  useEffect(() => {
    if (!isAuthenticated) return;
    refreshUserFiles();
    refreshCollections();
    // Intentionally only on mount + auth transition — refresh callbacks change
    // identity on every token refresh, which we don't want as a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // File upload handlers (for anonymous users)
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const audioFiles = files.filter(file =>
      file.type.startsWith('audio/') || /\.(mp3|wav|m4a|aac|flac|ogg|opus|wma|aiff)$/i.test(file.name)
    );

    if (audioFiles.length > 0) {
      // Don't auto-clear completed uploads - let user see their upload history
      // and allow SignInToClaimBanner to detect completions
      addFiles(audioFiles, {
        sourceType: 'upload',
        // Backend determines permanent storage based on JWT plan claim
      });
    }
  };

  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      // Don't auto-clear completed uploads - let user see their upload history
      // and allow SignInToClaimBanner to detect completions
      addFiles(files, {
        sourceType: 'upload',
        // Backend determines permanent storage based on JWT plan claim
      });
    }
  };

  const hasUploads = queue.length > 0;

  // Trigger confetti for early supporters (once per session)
  useEffect(() => {
    // Check sessionStorage to see if we've already shown confetti this session
    const hasShownConfetti = sessionStorage.getItem('confettiShown');

    if (isAuthenticated && user && isBetaUser() && !hasShownConfetti) {
      // Mark as shown in sessionStorage (persists across component mounts)
      sessionStorage.setItem('confettiShown', 'true');

      // Slight delay to sync with greeting animation
      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.3 },
          colors: ['#ff4800', '#06b6d4', '#fbbf24', '#f97316'], // soundwave orange, neon cyan, gold, orange
          ticks: 200,
          gravity: 0.8,
          scalar: 1.2
        });
      }, 400);
    }
  }, [isAuthenticated, user, isBetaUser]);

  return (
    <div className="min-h-screen relative overflow-hidden transition-colors" style={{ backgroundColor: 'var(--background-primary)' }}>
      <Header />

      {/* Ambient background effects (dark mode only) */}
      {theme === 'dark' && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-soundwave-900/20 via-transparent to-neon-900/20" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-soundwave-500/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-neon-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        </>
      )}

      <div className="relative z-10 container mx-auto px-4 py-12 max-w-7xl pt-20">
        {/* Early Supporter Greeting - Only shown to beta users */}
        {isAuthenticated && user && isBetaUser() && (
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              className="mt-4 inline-flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 py-1.5 md:py-2 rounded-full glass border border-soundwave-500/30"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Sparkles className="w-3.5 h-3.5 md:w-4 md:h-4 text-soundwave-500 flex-shrink-0" />
              <span className="text-xs md:text-sm text-neutral-300">
                {/* Mobile: Short version */}
                <span className="md:hidden">Early supporter 🎉</span>
                {/* Desktop: Full version */}
                <span className="hidden md:inline">
                  Thanks for being an early supporter, {user.given_name || user.name || 'friend'}! 🎉
                </span>
              </span>
            </motion.div>
          </motion.div>
        )}

        {/* Authenticated User Dashboard OR Anonymous Upload Section */}
        {isAuthenticated ? (
          /* Authenticated User Dashboard */
          <motion.div
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {!loadingUserFiles && userFiles.length === 0 ? (
              // First-time / zero-files state: skip the zero-tiles, the
              // create-collection pitch, the community chart and the Pro upsell.
              // One focused signal — upload your first file.
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-16"
              >
                <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-br from-soundwave-500/20 to-neon-500/20 flex items-center justify-center">
                  <Upload className="w-16 h-16 text-soundwave-500" />
                </div>
                <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Upload your first audio file
                </h2>
                <p className="text-lg mb-8" style={{ color: 'var(--text-secondary)' }}>
                  Drop in a track and we'll give you a shareable link in seconds.
                </p>
                <Link
                  to="/upload"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-soundwave-500 to-neon-500 hover:from-soundwave-600 hover:to-neon-600 text-white font-semibold transition-all shadow-lg"
                >
                  <Upload className="w-5 h-5" />
                  <span>Upload a file</span>
                </Link>
              </motion.div>
            ) : (
              <>
                {/* Free-tier top slot — a single card resolved from a
                    category-ordered registry of banner specs. Welcome beats
                    educational beats nudge; pre-emptions (Pro/Beta tier,
                    claimed-files prompt) live inside the slot. Add a banner
                    by writing the component, exporting its spec, and adding
                    it to HomeTopSlot's registry — no edits here. */}
                <HomeTopSlot
                  userFiles={userFiles}
                  expiringFiles={expiringFiles}
                  expiringCount={expiringCount}
                  loadingUserFiles={loadingUserFiles}
                />

                {/* Plays This Week — dopamine hero. */}
                {isAuthenticated && (
                  <div className="mb-6">
                    <PlaysThisWeekHero />
                  </div>
                )}

                {/* Personal storage — single source of truth for permanent-quota usage.
                    Same Pro/Beta gate as /upload; free users have no personal quota.
                    Landing renders the slim compact variant so the storage band
                    stops dominating above the fold; /my-files still gets the
                    full card. */}
                {isAuthenticated && (isProUser() || isBetaUser()) && (
                  <div className="mb-6">
                    <PersonalStorageCard compact />
                  </div>
                )}

                {/* Quick Stats - Show for all authenticated users */}
                <QuickStats />

                {/* Post-claim nudge: offer to group 2+ just-claimed files into a
                    collection. Appears right after a multi-file claim. Sits above the
                    dashboard grid, just over the Recent Files where those files
                    highlight. Visibility owned by useClaimedFilesPrompt above. */}
                {claimedFilesPrompt && (
                  <ClaimedFilesCollectionPrompt
                    files={claimedFilesPrompt.files}
                    onClear={clearClaimedFilesPrompt}
                  />
                )}

                {/* Dashboard Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  {/* Recent Files - Left column */}
                  <div className="lg:col-span-1">
                    <RecentFiles />
                  </div>

                  <div className="lg:col-span-1">
                    <RecentCollections limit={5} showViewAll={true} />
                  </div>

                  {/* Community Views - Full width below, only for free users */}
                  {/* Pro and Beta users have personal storage, don't need community stats */}
                  {!isProUser() && !isBetaUser() && (
                    <div className="lg:col-span-2">
                      <Suspense fallback={<div className="rounded-2xl border p-6 animate-pulse shadow-sm" style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border-color)' }}><div className="h-4 bg-neutral-800 rounded mb-6 w-1/3"></div><div className="h-32 bg-neutral-800 rounded"></div></div>}>
                        <CommunityViewsChart />
                      </Suspense>
                    </div>
                  )}

                  {/* What's New - Temporarily hidden to reduce noise */}
                  {/* <div className="lg:col-span-2">
                    <WhatsNew />
                  </div> */}
                </div>
              </>
            )}
          </motion.div>
        ) : (
          /* Anonymous User Upload Section */
          <>
            {/* Hero Section - Now includes integrated storage stats */}
            <motion.div
              className="mb-8 max-w-6xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Hero />
            </motion.div>

            {/* Hero Player Demo — preview of what a shared track looks like */}
            <motion.div
              className="mb-8 max-w-6xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <HeroPlayerDemo fileId="6563f10f-cce6-4b66-bd2c-0993784a4553" />
            </motion.div>

            {/* Try Free Section - Upload Zone */}
            <motion.div
              className="mb-8 max-w-6xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {/* Multi-file Upload Zone */}
              <div className="space-y-6">
                {/* Dropzone - Collapsible when uploads active */}
                <UploadZone
                  onFileSelect={handleFileSelect}
                  isUploading={false}
                  isDragging={isDragging}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  multiple={true}
                  collapsed={hasUploads}
                  isAuthenticated={isAuthenticated}
                />

                {/* Upload Queue - Show all files in unified card */}
                <UnifiedUploadCard
                  files={queue}
                  onRetry={retryFile}
                  onDismiss={removeFile}
                />

                {/* Sign In to Claim Banner - Show after uploads complete */}
                <div className="mt-6">
                  <SignInToClaimBanner />
                </div>
              </div>
            </motion.div>

            {/* Community Views - Show below upload zone for anonymous users */}
            <motion.div
              className="mt-12 max-w-6xl mx-auto space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Suspense fallback={<div className="rounded-2xl border p-6 animate-pulse shadow-sm" style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border-color)' }}><div className="h-4 bg-neutral-800 rounded mb-6 w-1/3"></div><div className="h-32 bg-neutral-800 rounded"></div></div>}>
                <CommunityViewsChart />
              </Suspense>
              <CommunityPool compact />
              {/* <WhatsNew /> */}
            </motion.div>
          </>
        )}

        {/* Plans Section - Hidden for now */}
        {/* <motion.div
          id="plans-section"
          className="mt-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <Plans />
        </motion.div> */}

        {/* FAQ Section */}
        <section className="max-w-6xl mx-auto mt-24 mb-12">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {[
              {
                question: "What is community-shared storage?",
                answer: (
                  <>
                    <p className="mb-4">
                      The free tier runs on a <strong>1TB shared pool</strong> that all free uploads draw from together, rather than a per-user quota. When you upload a file, it takes a slice of that pool; when it expires (<strong>7 days anonymous, 14 days signed-in</strong>), the slice returns. No quota math, no "you've used 4% of your allowance" nags.
                    </p>
                    <p>
                      The shared pool is our commitment to providing zero friction file sharing for everyone — drop a track, get a real player URL, share it. For tracks you want to keep past the retention window: <strong>Pro ($5/month)</strong> gives you <strong>{PRO_STORAGE_GB}GB of your own permanent storage</strong>, 320kbps audio, 1500MB file uploads, the recorder, the editor, and a public <strong>/@handle</strong> profile.
                    </p>
                  </>
                )
              },
              {
                question: "How long are files stored?",
                answer: (
                  <>
                    Every uploaded audio file is <strong>automatically converted to 128kbps MP3</strong> and stored based on your account type: <strong>Anonymous uploads are kept for 7 days</strong>, while <strong>signed-in free users get 14 days</strong>. Pro users enjoy <strong>permanent storage</strong> (up to {PRO_STORAGE_GB}GB, ~{PRO_STORAGE_HOURS_320KBPS} hours of 320kbps audio) with no expiration. After files expire, they're automatically deleted to free up space for new uploads. <strong>Sign in to double your retention to 14 days; go Pro to keep files permanently.</strong> Perfect for <strong>temporary sharing</strong> – podcasters sharing episode drafts, musicians sending demo tracks, or anyone who needs quick audio file sharing.
                  </>
                )
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="rounded-2xl border overflow-hidden"
                style={{
                  backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.6)',
                  borderColor: 'var(--border-color)'
                }}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:opacity-80 transition-opacity"
                >
                  <h3 className="text-lg font-semibold pr-4" style={{ color: 'var(--text-primary)' }}>
                    {faq.question}
                  </h3>
                  <ChevronDown className="w-5 h-5 flex-shrink-0" style={{
                    color: 'var(--text-tertiary)',
                    transform: expandedFaq === index ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }} />
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-5 pt-0 border-t" style={{ borderColor: 'var(--border-color)' }}>
                    <p className="leading-relaxed mt-4" style={{ color: 'var(--text-secondary)' }}>
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

      </div>

      <Footer />
    </div>
  );
};

export default Home;